import { Stack } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, View } from 'react-native';

import { PageHeaderRow } from '@/components/page-header-row';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { BRAND } from '@sixam/config/brand';
import { authedFetch } from '@/lib/authed-fetch';
import { getCurrentTime } from '@/lib/dev-clock';
import { REPORT_REASONS, type ReportReason } from '@/lib/moderation';

type QueuedReport = Readonly<{
  created_at: string;
  details: string | null;
  id: string;
  reason: ReportReason;
  reported_excerpt: string | null;
  reporterName: string;
  subject_type: string;
  subjectUserName: string | null;
}>;

const REASON_LABEL = new Map(REPORT_REASONS.map((option) => [option.value, option.label]));

function hoursSince(iso: string, now: Date): number {
  return Math.floor((now.getTime() - Date.parse(iso)) / 3_600_000);
}

export default function ModerationQueueScreen() {
  const [reports, setReports] = React.useState<QueuedReport[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [workingId, setWorkingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const now = getCurrentTime();

  const [reloadToken, setReloadToken] = React.useState(0);

  // The loader lives inside the effect, which is how the rest of this codebase
  // avoids a setState that fires synchronously on mount.
  React.useEffect(() => {
    let active = true;

    async function load() {
      setIsLoading(true);
      try {
        const response = await authedFetch('/api/moderation/queue');
        const payload = (await response.json()) as { error?: string; reports?: QueuedReport[] };
        if (!response.ok) throw new Error(payload.error ?? 'Could not load reports.');
        if (active) {
          setReports(payload.reports ?? []);
          setError(null);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load reports.');
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [reloadToken]);

  async function resolve(
    report: QueuedReport,
    options: Readonly<{
      removeContent: boolean;
      resolution: 'actioned' | 'dismissed';
      suspendAuthor: boolean;
    }>
  ) {
    setWorkingId(report.id);
    try {
      const response = await authedFetch('/api/moderation/queue', {
        body: JSON.stringify({ reportId: report.id, ...options }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error ?? 'Could not resolve the report.');
      }
      setReports((current) => current.filter((row) => row.id !== report.id));
    } catch (resolveError) {
      setError(resolveError instanceof Error ? resolveError.message : 'Could not resolve.');
    } finally {
      setWorkingId(null);
    }
  }

  return (
    <>
      <Stack.Screen options={{ title: 'Reports' }} />
      <ScrollView
        className="flex-1 bg-background"
        contentContainerClassName="gap-4 p-6"
        refreshControl={
          <RefreshControl
            colors={[BRAND.color.orange]}
            onRefresh={() => setReloadToken((token) => token + 1)}
            refreshing={isLoading}
            tintColor={BRAND.color.orange}
          />
        }
      >
        <PageHeaderRow title="Reports" subtitle="Act within 24 hours" backHref="/admin" />

        {error ? (
          <Text className="text-destructive" selectable>
            {error}
          </Text>
        ) : null}

        {isLoading && reports.length === 0 ? <ActivityIndicator /> : null}

        {!isLoading && reports.length === 0 ? (
          <View className="items-center gap-2 py-16">
            <Text className="font-bold">Nothing to review.</Text>
            <Text variant="muted" className="text-center">
              Reported content and blocked members show up here.
            </Text>
          </View>
        ) : null}

        {reports.map((report) => {
          const age = hoursSince(report.created_at, now);
          const isWorking = workingId === report.id;
          return (
            <View className="gap-3 rounded-md border-2 border-border bg-card p-4" key={report.id}>
              <View className="flex-row items-start justify-between gap-3">
                <View className="min-w-0 flex-1">
                  <Text className="font-bold">
                    {REASON_LABEL.get(report.reason) ?? report.reason}
                  </Text>
                  <Text variant="muted">
                    {report.subject_type} · reported by {report.reporterName}
                    {report.subjectUserName ? ` · posted by ${report.subjectUserName}` : ''}
                  </Text>
                </View>
                {/* Past the promise: this one is the reason the queue is sorted oldest first. */}
                <Text
                  className={age >= 24 ? 'font-bold text-destructive' : 'text-muted-foreground'}
                >
                  {age}h
                </Text>
              </View>

              {report.reported_excerpt ? (
                <Text className="rounded-md bg-muted p-3" selectable>
                  {report.reported_excerpt}
                </Text>
              ) : null}
              {report.details ? <Text variant="muted">{report.details}</Text> : null}

              <View className="gap-2">
                <Button
                  disabled={isWorking}
                  onPress={() =>
                    void resolve(report, {
                      removeContent: true,
                      resolution: 'actioned',
                      suspendAuthor: true,
                    })
                  }
                  variant="destructive"
                >
                  <Text>Remove content and eject member</Text>
                </Button>
                <Button
                  disabled={isWorking}
                  onPress={() =>
                    void resolve(report, {
                      removeContent: true,
                      resolution: 'actioned',
                      suspendAuthor: false,
                    })
                  }
                  variant="secondary"
                >
                  <Text>Remove content only</Text>
                </Button>
                <Button
                  disabled={isWorking}
                  onPress={() =>
                    void resolve(report, {
                      removeContent: false,
                      resolution: 'dismissed',
                      suspendAuthor: false,
                    })
                  }
                  variant="ghost"
                >
                  <Text>Dismiss</Text>
                </Button>
              </View>
            </View>
          );
        })}
      </ScrollView>
    </>
  );
}
